import React from 'react';
import { EnvelopeIcon, MapPinIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { telHref, useSiteSettings } from '../context/SiteSettingsContext.tsx';

interface Props {
  variant?: 'light' | 'dark';
  direction?: 'row' | 'col';
  className?: string;
}

// Телефон, email и местоположение из настроек сайта; пустое поле — виджет не показывается.
// Email намеренно простым текстом, без mailto (см. CLAUDE.md, п. про 152-ФЗ).
const ContactWidgets: React.FC<Props> = ({ variant = 'light', direction = 'row', className = '' }) => {
  const { settings } = useSiteSettings();
  const dark = variant === 'dark';

  const items = [
    {
      key: 'phone',
      label: 'Телефон',
      value: settings.phone,
      href: settings.phone ? telHref(settings.phone) : undefined,
      Icon: PhoneIcon,
      tone: dark ? 'bg-white/10 text-accent-400' : 'bg-blue-100 text-blue-600',
    },
    {
      key: 'email',
      label: 'Email',
      value: settings.email,
      href: undefined,
      Icon: EnvelopeIcon,
      tone: dark ? 'bg-white/10 text-accent-400' : 'bg-green-100 text-green-600',
    },
    {
      key: 'location',
      label: 'Местоположение',
      value: settings.location,
      href: undefined,
      Icon: MapPinIcon,
      tone: dark ? 'bg-white/10 text-accent-400' : 'bg-purple-100 text-purple-600',
    },
  ].filter((i) => i.value);

  if (items.length === 0) return null;

  const card = dark
    ? 'bg-white/5 ring-1 ring-white/10'
    : 'bg-white shadow-sm ring-1 ring-secondary-200';
  const valueCls = dark ? 'text-white' : 'text-secondary-900';

  return (
    <div className={`flex ${direction === 'col' ? 'flex-col' : 'flex-row flex-wrap'} gap-3 ${className}`}>
      {items.map(({ key, label, value, href, Icon, tone }) => {
        const content = (
          <>
            <span className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${tone}`}>
              <Icon className="w-5 h-5" />
            </span>
            <span className={`min-w-0 text-sm font-semibold break-words ${valueCls}`}>
              <span className="sr-only">{label}: </span>
              {value}
            </span>
          </>
        );
        const cls = `flex items-center gap-3 rounded-xl px-4 py-3 ${card}`;
        return href ? (
          <a key={key} href={href} className={`${cls} hover:shadow-md transition`}>
            {content}
          </a>
        ) : (
          <div key={key} className={cls}>
            {content}
          </div>
        );
      })}
    </div>
  );
};

export default ContactWidgets;
